import {
  type DetailedPoliceReport,
  type PoliceReportAttestationStatus,
} from '@/services/api/generated/policeReportsService';

/**
 * Attestation status priority (higher = more qualified)
 */
const ATTESTATION_PRIORITY: Record<PoliceReportAttestationStatus, number> = {
  verified_received: 5,
  attested_verified: 4,
  attested_unverified: 3,
  unclear: 2,
  inferred: 1,
  rejected_verified: 0,
};

export interface InvolvedAgencySummary {
  type: 'none' | 'tbd' | 'single' | 'multiple' | 'unclear' | 'received';
  message: string;
  primaryReports: DetailedPoliceReport[];
  secondaryReports: DetailedPoliceReport[];
  allReports: DetailedPoliceReport[];
}

/**
 * Analyzes police reports to determine involved agency status and
 * intelligently filters/groups them for display
 */
export function analyzeInvolvedAgency(
  reports: DetailedPoliceReport[],
): InvolvedAgencySummary {
  if (!reports || reports.length === 0) {
    return {
      type: 'none',
      message: 'No agencies identified',
      primaryReports: [],
      secondaryReports: [],
      allReports: [],
    };
  }

  // Filter out rejected unless they're all we have
  const nonRejected = reports.filter(
    (r) => r.attestation_status !== 'rejected_verified',
  );
  const workingSet = nonRejected.length > 0 ? nonRejected : reports;

  // Find the highest attestation status
  const maxPriority = Math.max(
    ...workingSet.map(
      (r) =>
        ATTESTATION_PRIORITY[
          r.attestation_status as PoliceReportAttestationStatus
        ] ?? 0,
    ),
  );

  // Get reports with the highest priority
  const primaryReports = workingSet.filter(
    (r) =>
      (ATTESTATION_PRIORITY[
        r.attestation_status as PoliceReportAttestationStatus
      ] ?? 0) === maxPriority,
  );

  // All others are secondary
  const secondaryReports = reports.filter((r) => !primaryReports.includes(r));

  // Determine the type and message
  const primaryStatus = primaryReports[0]
    ?.attestation_status as PoliceReportAttestationStatus;

  // All inferred - system guesses
  if (
    primaryStatus === 'inferred' &&
    primaryReports.length === workingSet.length
  ) {
    return {
      type: 'tbd',
      message:
        'Involved Department: system-generated leads based on incident location',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Single verified received - we have the report (green/success state)
  if (primaryReports.length === 1 && primaryStatus === 'verified_received') {
    return {
      type: 'received',
      message: 'Involved Department: report received',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Multiple verified received - unclear which is the real one
  if (primaryReports.length > 1 && primaryStatus === 'verified_received') {
    return {
      type: 'multiple',
      message: 'Involved Department: unclear, multiple reports received',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Single verified/attested agency
  if (primaryReports.length === 1 && maxPriority >= 4) {
    return {
      type: 'single',
      message: 'Involved Department: confirmed',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Single unverified but attested
  if (primaryReports.length === 1 && maxPriority === 3) {
    return {
      type: 'single',
      message: 'Involved Department: likely, awaiting verification',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Multiple qualified agencies - ambiguous
  if (primaryReports.length > 1 && maxPriority >= 3) {
    return {
      type: 'multiple',
      message: 'Involved Department: unclear, multiple agencies indicated',
      primaryReports,
      secondaryReports,
      allReports: reports,
    };
  }

  // Unclear/low quality
  return {
    type: 'unclear',
    message: 'Involved Department: investigation in progress',
    primaryReports,
    secondaryReports,
    allReports: reports,
  };
}

/**
 * Determines if a report should be visually de-emphasized
 */
export function shouldMinimize(
  report: DetailedPoliceReport,
  summary: InvolvedAgencySummary,
): boolean {
  // Always minimize rejected
  if (report.attestation_status === 'rejected_verified') {
    return true;
  }

  // Minimize secondary reports if we have strong primary reports
  if (summary.type === 'single' || summary.type === 'multiple') {
    return summary.secondaryReports.includes(report);
  }

  return false;
}
